/**
 * pedidoAutoPrint.service.ts — Preferencias y cola de impresión de pedidos de tienda.
 *
 * QUÉ HACE:
 *  - Persiste en transactionalStore (ACID) las preferencias de auto-impresión de
 *    pedidos «para el local» (on/off) y la cola de impresión con estado por
 *    pedido (pendiente / impreso / fallo), scoped por businessId.
 *  - Expone helpers de reintento con backoff (fallo) e idempotencia por pedido.id.
 *
 * QUÉ EXPORTA: PedidoAutoPrintPrefs, EstadoImpresionPedido, ColaImpresionEntry,
 *   getPedidoAutoPrintPrefs, setPedidoAutoPrintPrefs,
 *   getPedidoAutoPrintQueue, upsertPedidoImpresion, prunePedidoImpresionQueue,
 *   shouldRetryFallo
 *
 * QUÉ NO DEBE HACER:
 *  - No imprime: la impresión la dispara el watcher (usePedidoAutoPrintWatcher).
 *  - No usa localStorage directo (prohibido): todo pasa por transactionalStore.
 *  - No contiene React ni lógica de UI.
 */
import { txRead, txWrite } from "@/lib/persistence/transactionalStore";

export interface PedidoAutoPrintPrefs {
  /** Master switch: si está OFF no se auto-imprime ni se encolan pedidos nuevos. */
  autoPrintEnabled: boolean;
}

export type EstadoImpresionPedido = "pendiente" | "impreso" | "fallo";

export interface ColaImpresionEntry {
  pedidoId: string;
  estadoImpresion: EstadoImpresionPedido;
  /** Veces que se intentó imprimir sin éxito. */
  intentos: number;
  /** ISO del último intento (null si nunca se intentó). */
  ultimoIntento: string | null;
  /** Error del último fallo (para diagnóstico). */
  ultimoError: string | null;
}

/** Defaults: ON para el dueño (configurable en Preferencias). */
const PEDIDO_AUTO_PRINT_DEFAULTS: PedidoAutoPrintPrefs = {
  autoPrintEnabled: true,
};

/** Límite de entradas en la cola (regla de costos: no crecer sin control). */
const MAX_QUEUE_ENTRIES = 50;
/** Reintentos máximos para un fallo de impresión antes de abandonar. */
const MAX_INTENTOS_FALLO = 5;
/** Espera entre reintentos de un fallo de impresión. */
const RETRY_FALLO_BACKOFF_MS = 2 * 60_000;

const prefsKey = (businessId: string): string =>
  `pedido_auto_print_prefs:${businessId}`;
const queueKey = (businessId: string): string =>
  `pedido_auto_print_queue:${businessId}`;

/** Lee las preferencias de auto-impresión del negocio. */
export function getPedidoAutoPrintPrefs(
  businessId: string,
): PedidoAutoPrintPrefs {
  const stored = txRead<Partial<PedidoAutoPrintPrefs>>(
    prefsKey(businessId),
    {},
  );
  const valid =
    typeof stored === "object" && stored !== null && !Array.isArray(stored)
      ? stored
      : {};
  return {
    autoPrintEnabled:
      valid.autoPrintEnabled ?? PEDIDO_AUTO_PRINT_DEFAULTS.autoPrintEnabled,
  };
}

/** Actualiza y persiste las preferencias de auto-impresión; devuelve el nuevo estado. */
export function setPedidoAutoPrintPrefs(
  businessId: string,
  patch: Partial<PedidoAutoPrintPrefs>,
): PedidoAutoPrintPrefs {
  const next = { ...getPedidoAutoPrintPrefs(businessId), ...patch };
  txWrite(prefsKey(businessId), next);
  return next;
}

/** Lee la cola de impresión pendiente del negocio. */
export function getPedidoAutoPrintQueue(
  businessId: string,
): ColaImpresionEntry[] {
  const stored = txRead<unknown>(queueKey(businessId), []);
  return Array.isArray(stored) ? (stored as ColaImpresionEntry[]) : [];
}

/**
 * Inserta o actualiza la entrada de un pedido en la cola (idempotente por
 * pedido.id). `meta` sobrescribe los campos de intento/error de la entrada.
 */
export function upsertPedidoImpresion(
  businessId: string,
  pedidoId: string,
  estadoImpresion: EstadoImpresionPedido,
  meta?: {
    intentos?: number;
    ultimoIntento?: string;
    ultimoError?: string | null;
  },
): void {
  const cola = getPedidoAutoPrintQueue(businessId);
  const prev = cola.find((e) => e.pedidoId === pedidoId);
  const entry: ColaImpresionEntry = {
    pedidoId,
    estadoImpresion,
    intentos: meta?.intentos ?? prev?.intentos ?? 0,
    ultimoIntento: meta?.ultimoIntento ?? prev?.ultimoIntento ?? null,
    ultimoError:
      meta?.ultimoError !== undefined
        ? meta.ultimoError
        : (prev?.ultimoError ?? null),
  };
  const idx = cola.findIndex((e) => e.pedidoId === pedidoId);
  if (idx !== -1) {
    cola[idx] = entry;
  } else {
    cola.push(entry);
  }
  txWrite(queueKey(businessId), cola);
}

/**
 * Poda la cola: elimina entradas de pedidos que ya no están activos (terminales)
 * y recorta al límite. Se invoca en cada tick del watcher.
 */
export function prunePedidoImpresionQueue(
  businessId: string,
  pedidosActivos: ReadonlyArray<{ id: string }>,
): void {
  const cola = getPedidoAutoPrintQueue(businessId);
  const activos = new Set(pedidosActivos.map((p) => p.id));
  const filtrada = cola.filter((e) => activos.has(e.pedidoId));
  if (filtrada.length === cola.length) return;
  txWrite(queueKey(businessId), filtrada.slice(-MAX_QUEUE_ENTRIES));
}

/** true si un fallo de impresión debe reintentarse (backoff 2 min, máx 5 intentos). */
export function shouldRetryFallo(
  entry: ColaImpresionEntry,
  now: Date,
): boolean {
  if (entry.estadoImpresion !== "fallo") return false;
  if (entry.intentos >= MAX_INTENTOS_FALLO) return false;
  if (!entry.ultimoIntento) return true;
  const last = new Date(entry.ultimoIntento).getTime();
  if (Number.isNaN(last)) return true;
  return now.getTime() - last >= RETRY_FALLO_BACKOFF_MS;
}
